import React, {useEffect, useState} from 'react';
import {Box, InputAdornment, MenuItem, Stack, TextField, Typography} from '@mui/material';
import {useGetDosageUnitsQuery, useGetMedicineLookupsQuery} from '../../../../../api/services/medicinesService';
import {countInUnit} from '../../../../../utility/medicines';

/* eslint-disable react/prop-types */

// The Medicine Dispense question on a staff form linked to a child (AD-2581, 7 October 2026). The member
// of staff chooses from the child's own medicines in stock, one line per medicine, brand and unit, and
// enters the amount, the date and time given and a note. The dose is recorded as Dispense on the
// child's Medical tab records it. A question built before then names one medicine and takes a number.

// The stored answer is a readable line, for responses and PDFs; the chosen medicine travels beside it.
export const doseKey = componentId => `${componentId}__dose`;

const nowForInput = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    return new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
};

const describeLine = (line) => (line.brandName ? `${line.medicine} (${line.brandName})` : line.medicine);

const MedicineDispense = (
    {
        onChange,
        componentId,
        value,
        label,
        medicineLookupId,
        isEditMode,
        readonly,
        submitMode,
        previewMode,
        placeholderValues
    }) => {
    const {data: everyMedicine = []} = useGetMedicineLookupsQuery(undefined, {skip: !isEditMode && !medicineLookupId});
    const {data: units = []} = useGetDosageUnitsQuery();
    const medicines = everyMedicine.filter(medicine => medicine.isActive !== false || medicine.id === medicineLookupId);
    const chosenLookup = medicines.find(medicine => medicine.id === medicineLookupId);
    const held = Array.isArray(placeholderValues?.medicinesHeld) ? placeholderValues.medicinesHeld : null;
    const lines = (held ?? []).filter(line => !medicineLookupId || line.medicineLookupId === medicineLookupId);
    const [entry, setEntry] = useState({studentMedicineId: '', dosesGiven: '', givenAt: nowForInput(), note: ''});

    const count = (amount, unit) => countInUnit(Number(amount ?? 0), unit, units);
    const chosen = lines.find(line => line.studentMedicineId === entry.studentMedicineId);
    const amount = Number(entry.dosesGiven);
    const problemWithAmount = () => {
        if (entry.dosesGiven === '') {
            return '';
        }
        if (!Number.isInteger(amount) || amount < 1) {
            return 'Enter a whole number, 1 or more';
        }
        return chosen && amount > chosen.dosesHeld ? `Only ${count(chosen.dosesHeld, chosen.dosageUnit)} left at school` : '';
    };
    const amountProblem = problemWithAmount();
    const timeProblem = entry.givenAt && entry.givenAt > nowForInput() ? 'The time given cannot be in the future' : '';

    // The answer is complete only with a medicine, a valid amount and a time; otherwise it is blank.
    useEffect(() => {
        if (!submitMode || held === null) {
            return;
        }

        const complete = chosen && entry.dosesGiven !== '' && !amountProblem && entry.givenAt && !timeProblem;
        const when = complete ? new Date(entry.givenAt) : null;
        const summary = complete
            ? `${describeLine(chosen)}: ${count(amount, chosen.dosageUnit)} given on ${when.toLocaleString('en-GB', {day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'})}${entry.note.trim() ? `. Note: ${entry.note.trim()}` : ''}`
            : '';
        onChange({id: componentId, value: summary});
        onChange({
            id: doseKey(componentId),
            value: complete
                ? {studentMedicineId: chosen.studentMedicineId, dosesGiven: amount, givenAt: when.toISOString(), note: entry.note.trim()}
                : null
        });
        // onChange is a new function on every render of the form, so it is left out on purpose.
    }, [entry, submitMode, held === null, chosen, amountProblem, timeProblem]);

    if (isEditMode) {
        return (
            <Stack gap={2} data-testid={`dispense-settings-${componentId}`}>
                <TextField
                    fullWidth
                    label="Question"
                    value={label ?? ''}
                    data-testid={`dispense-label-${componentId}`}
                    onChange={(event) => onChange({id: componentId, label: event.target.value})}
                />
                <TextField
                    select
                    fullWidth
                    label="Medicines offered"
                    SelectProps={{displayEmpty: true}}
                    InputLabelProps={{shrink: true}}
                    value={medicineLookupId ?? ''}
                    data-testid={`dispense-medicine-${componentId}`}
                    onChange={(event) => onChange({id: componentId, medicineLookupId: event.target.value || null})}
                >
                    <MenuItem value="">Every medicine the child holds</MenuItem>
                    {medicines.map(medicine => (
                        <MenuItem
                            key={medicine.id}
                            value={medicine.id}
                            data-testid={`dispense-medicine-option-${medicine.id}`}
                        >
                            {medicine.description}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
        );
    }

    // Read back after submission, in the response and the PDF.
    if (readonly || held === null) {
        return (
            <Box data-testid={`dispense-${componentId}`}>
                <Typography variant="body1" sx={{mb: 1}}>
                    {label || `Doses of ${chosenLookup?.description ?? 'this medicine'} given`}
                </Typography>
                {typeof value === 'string' && value && !/^\d+$/.test(value)
                    ? <Typography variant="body2" data-testid={`dispense-answer-${componentId}`}>{value}</Typography>
                    : (
                        <TextField
                            type="number"
                            size="small"
                            disabled={readonly || (!submitMode && !previewMode)}
                            inputProps={{min: 1}}
                            sx={{maxWidth: 200}}
                            data-testid={`dispense-doses-${componentId}`}
                            value={value ?? ''}
                            onChange={(event) => onChange({id: componentId, value: event.target.value})}
                        />
                    )}
            </Box>
        );
    }

    return (
        <Box data-testid={`dispense-${componentId}`}>
            <Typography variant="body1" sx={{mb: 1}}>{label || 'Medicine given'}</Typography>
            {lines.length === 0
                ? (
                    <Typography variant="body2" color="error" data-testid={`dispense-none-${componentId}`}>
                        {placeholderValues?.studentForename || 'This child'} does not have any medicine in stock at school.
                    </Typography>
                )
                : (
                    <Stack gap={2}>
                        <TextField
                            select
                            fullWidth
                            label="Medicine"
                            value={entry.studentMedicineId}
                            inputProps={{'data-testid': `dispense-choice-${componentId}`}}
                            onChange={(event) => setEntry({...entry, studentMedicineId: event.target.value})}
                        >
                            {lines.map(line => (
                                <MenuItem key={line.studentMedicineId} value={line.studentMedicineId}>
                                    {describeLine(line)}: {count(line.dosesHeld, line.dosageUnit)} left
                                </MenuItem>
                            ))}
                        </TextField>
                        <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                            <TextField
                                type="number"
                                fullWidth
                                label="Amount given"
                                inputProps={{min: 1, step: 1, 'data-testid': `dispense-amount-${componentId}`}}
                                InputProps={{
                                    endAdornment: chosen && (
                                        <InputAdornment position="end" sx={{'& p': {color: 'text.primary'}}}>
                                            {count(2, chosen.dosageUnit).replace(/^2 /, '')}
                                        </InputAdornment>
                                    )
                                }}
                                error={Boolean(amountProblem)}
                                helperText={amountProblem}
                                value={entry.dosesGiven}
                                onChange={(event) => setEntry({...entry, dosesGiven: event.target.value})}
                            />
                            <TextField
                                type="datetime-local"
                                fullWidth
                                label="Date and time given"
                                InputLabelProps={{shrink: true}}
                                inputProps={{max: nowForInput(), 'data-testid': `dispense-given-at-${componentId}`}}
                                error={Boolean(timeProblem)}
                                helperText={timeProblem}
                                value={entry.givenAt}
                                onChange={(event) => setEntry({...entry, givenAt: event.target.value})}
                            />
                        </Stack>
                        <TextField
                            fullWidth
                            label="Note"
                            inputProps={{maxLength: 1000, 'data-testid': `dispense-note-${componentId}`}}
                            value={entry.note}
                            onChange={(event) => setEntry({...entry, note: event.target.value})}
                        />
                    </Stack>
                )}
        </Box>
    );
};

export default MedicineDispense;
